import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { useLang, useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const links = [
    { to: "/", label: t.nav.home },
    { to: "/how-it-works", label: t.nav.how },
    { to: "/licensing", label: t.nav.licensing },
    { to: "/impact", label: t.nav.impact },
    { to: "/contact", label: t.nav.contact },
  ];

  // Permanent admin entry point (owner request): one click opens the admin panel,
  // logging in with the quick-admin account first when needed
  const adminAccess = async () => {
    if (user?.is_admin) {
      navigate("/admin");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: "admin", password: "dekrbat" }),
      });
      if (!r.ok) throw new Error("admin login failed");
      const data = await r.json();
      setUser(data.user);
      navigate("/admin");
    } catch {
      navigate("/");
    } finally {
      setBusy(false);
    }
  };
  return (
    <footer className="bg-slate-900 text-slate-200" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          <div className="max-w-sm">
            <Logo dark />
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{t.footer.tagline}</p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.22em] text-sky-400">
              {t.footer.location}
            </p>
          </div>
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              {t.footer.nav}
            </p>
            <nav className="mt-4 flex flex-col gap-2.5" aria-label="Footer">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-sm text-slate-300 transition-colors duration-300 hover:text-sky-400"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-2 border-t border-slate-800 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} Dekrbat Pure. {t.footer.rights}
          </p>
          <div className="flex items-center gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              CO₂ · IoT · Microalgae
            </p>
            <button
              data-testid="footer-admin-btn"
              onClick={adminAccess}
              disabled={busy}
              title="Admin"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors duration-300 hover:border-sky-400 hover:text-sky-400 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
              Admin
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
